import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { Suspense, lazy, useState } from 'react';
import { UserProvider, useUser } from './contexts/UserContext';
import AuthGuard from './components/AuthGuard';

// Lazy load pages
const HomePage = lazy(() => import('./pages/HomePage'));
const SeatLookupPage = lazy(() => import('./pages/SeatLookupPage'));
const PhotoGalleryPage = lazy(() => import('./pages/PhotoGalleryPage'));
const AssistantChatPage = lazy(() => import('./pages/AssistantChatPage'));
const ProgramPage = lazy(() => import('./pages/ProgramPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const PhotoUploadPage = lazy(() => import('./pages/PhotoUploadPage'));

// Loading component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
      <div className="text-center">
        <div className="w-8 h-8 border border-[#C41E3A]/30 border-t-[#C41E3A] rounded-full animate-spin mx-auto mb-6"></div>
        <p className="text-[#C41E3A]/50 text-xs tracking-[0.3em] uppercase">Loading</p>
      </div>
    </div>
  );
}

// Layout component
function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { name: userName, isAuthenticated } = useUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className={`min-h-screen flex flex-col ${isHomePage ? 'bg-[#FAFAF8]' : 'bg-[#FAFAF8]'}`}>
      {/* Header - Transparent on homepage, solid on other pages */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        isHomePage ? 'bg-transparent' : 'bg-[#FAFAF8]/95 backdrop-blur-sm border-b border-[#E5E5E5]'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-center h-20">
            {!isHomePage && (
              <Link to="/" className="flex items-center gap-3 group">
                <img
                  src="/axalta-logo.png"
                  alt="Axalta"
                  className="h-8 w-auto"
                />
                <img
                  src="/zero-incidents-logo.png"
                  alt="Zero Incidents"
                  className="h-8 w-auto"
                />
                <span className="hidden sm:block w-px h-4 bg-[#1a1a1a]/20"></span>
                <span className="hidden sm:block text-[10px] tracking-[0.2em] uppercase font-light text-[#666666]">
                  艾仕得年会
                </span>
              </Link>
            )}
            {isHomePage && <div></div>}

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link
                to="/seats"
                className={`text-xs transition-colors duration-500 tracking-[0.15em] uppercase font-light ${
                  isHomePage ? 'text-white/40 hover:text-white/80' : 'text-[#666666] hover:text-[#1a1a1a]'
                }`}
              >
                座位
              </Link>
              <Link
                to="/photos"
                className={`text-xs transition-colors duration-500 tracking-[0.15em] uppercase font-light ${
                  isHomePage ? 'text-white/40 hover:text-white/80' : 'text-[#666666] hover:text-[#1a1a1a]'
                }`}
              >
                照片
              </Link>
              <Link
                to="/program"
                className={`text-xs transition-colors duration-500 tracking-[0.15em] uppercase font-light ${
                  isHomePage ? 'text-white/40 hover:text-white/80' : 'text-[#666666] hover:text-[#1a1a1a]'
                }`}
              >
                节目单
              </Link>
              <Link
                to="/assistant"
                className={`text-xs transition-colors duration-500 tracking-[0.15em] uppercase font-light ${
                  isHomePage ? 'text-white/40 hover:text-white/80' : 'text-[#666666] hover:text-[#1a1a1a]'
                }`}
              >
                助手
              </Link>
              {isAuthenticated && userName && (
                <div className={`flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-light ${
                  isHomePage ? 'text-white/60' : 'text-[#D4AF37]'
                }`}>
                  <span className="material-symbols-outlined text-sm">person</span>
                  {userName}
                </div>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center transition-colors ${
                isHomePage ? 'text-white/60 hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]'
              }`}
              aria-label="菜单"
            >
              <span className="material-symbols-outlined text-2xl">
                {isMobileMenuOpen ? 'close' : 'menu'}
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Sidebar */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 w-64 bg-white shadow-2xl transform transition-transform duration-300 md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]">
            <span className="text-sm font-medium text-[#2d2d2d] tracking-wide">菜单</span>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-8 h-8 flex items-center justify-center text-[#666666] hover:text-[#1a1a1a]"
            >
              <span className="material-symbols-outlined text-xl">close</span>
            </button>
          </div>

          {/* Mobile Menu Items */}
          <nav className="flex-1 overflow-y-auto p-6">
            <div className="space-y-2">
              <Link
                to="/seats"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-[#666666] hover:text-[#C41E3A] hover:bg-[#C41E3A]/5 rounded-lg transition-colors"
              >
                <span className="material-symbols-outlined text-xl">event_seat</span>
                <span className="text-sm tracking-wide">座位</span>
              </Link>
              <Link
                to="/photos"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-[#666666] hover:text-[#C41E3A] hover:bg-[#C41E3A]/5 rounded-lg transition-colors"
              >
                <span className="material-symbols-outlined text-xl">photo_library</span>
                <span className="text-sm tracking-wide">照片</span>
              </Link>
              <Link
                to="/program"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-[#666666] hover:text-[#C41E3A] hover:bg-[#C41E3A]/5 rounded-lg transition-colors"
              >
                <span className="material-symbols-outlined text-xl">event_note</span>
                <span className="text-sm tracking-wide">节目单</span>
              </Link>
              <Link
                to="/assistant"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-[#666666] hover:text-[#C41E3A] hover:bg-[#C41E3A]/5 rounded-lg transition-colors"
              >
                <span className="material-symbols-outlined text-xl">smart_toy</span>
                <span className="text-sm tracking-wide">助手</span>
              </Link>
            </div>
          </nav>

          {/* Mobile Menu Footer - User Info */}
          {isAuthenticated && userName && (
            <div className="p-6 border-t border-[#E5E5E5]">
              <div className="flex items-center gap-3 px-4 py-3 bg-[#D4AF37]/10 rounded-lg">
                <span className="material-symbols-outlined text-xl text-[#D4AF37]">person</span>
                <span className="text-sm text-[#2d2d2d] font-medium">{userName}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 relative">{children}</main>

      {/* Footer - Minimal */}
      <footer className="border-t border-[#E5E5E5] py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col items-center justify-center gap-6">
            <div className="flex items-center gap-4">
              <img src="/axalta-logo.png" alt="Axalta" className="h-8 w-auto" />
              <img src="/zero-incidents-logo.png" alt="Zero Incidents" className="h-8 w-auto" />
            </div>
            <div className="flex items-center gap-4">
              <div className="w-8 h-px bg-[#D4AF37]/30"></div>
              <p className="text-[#999999] text-[10px] tracking-[0.2em] uppercase">
                艾仕得年会 System
              </p>
              <div className="w-8 h-px bg-[#D4AF37]/30"></div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <UserProvider>
      <Layout>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes with AuthGuard */}
            <Route path="/" element={<AuthGuard><HomePage /></AuthGuard>} />
            <Route path="/seats" element={<AuthGuard><SeatLookupPage /></AuthGuard>} />
            <Route path="/photos" element={<AuthGuard><PhotoGalleryPage /></AuthGuard>} />
            <Route path="/program" element={<AuthGuard><ProgramPage /></AuthGuard>} />
            <Route path="/assistant" element={<AuthGuard><AssistantChatPage /></AuthGuard>} />

            {/* Admin routes without AuthGuard */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/upload" element={<PhotoUploadPage />} />
          </Routes>
        </Suspense>
      </Layout>
    </UserProvider>
  );
}

export default App;
